import React from 'react';
import { formatCurrency as formatCurrencyFn, formatDate as formatDateFn } from '../utils/formatters';

const InvoiceTemplateCuentaCobro = ({ invoice, companyInfo, template, templateColor }) => {
  // Color dinámico: prioridad al color personalizado
  const primaryColor = templateColor || template?.color || '#84cc16';
  const userCountry = invoice?.from?.country || companyInfo?.country;
  
  // Función para convertir número a letras en español
  const numeroALetras = (num) => {
    const unidades = ['', 'UN', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
    const decenas = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
    const especiales = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
    const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];
    
    if (num === 0) return 'CERO';
    if (num === 100) return 'CIEN';
    
    let resultado = '';
    
    if (num >= 1000000) {
      const millones = Math.floor(num / 1000000);
      if (millones === 1) {
        resultado += 'UN MILLÓN ';
      } else {
        resultado += convertirCentenas(millones) + ' MILLONES ';
      }
      num %= 1000000;
    }
    
    if (num >= 1000) {
      const miles = Math.floor(num / 1000);
      if (miles === 1) {
        resultado += 'MIL ';
      } else {
        resultado += convertirCentenas(miles) + ' MIL ';
      }
      num %= 1000;
    }
    
    resultado += convertirCentenas(num);
    
    function convertirCentenas(n) {
      let res = '';
      if (n >= 100) {
        if (n === 100) return 'CIEN';
        res += centenas[Math.floor(n / 100)] + ' ';
        n %= 100;
      }
      if (n >= 10 && n < 20) {
        res += especiales[n - 10];
        return res.trim();
      }
      if (n >= 20) {
        res += decenas[Math.floor(n / 10)];
        if (n % 10 !== 0) {
          res += ' Y ' + unidades[n % 10];
        }
      } else if (n > 0) {
        res += unidades[n];
      }
      return res.trim();
    }
    
    return resultado.trim() + ' PESOS COLOMBIANOS';
  };

  const formatCurrency = (amount) => formatCurrencyFn(amount, {
    country: userCountry,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const formatDate = (dateString) => formatDateFn(dateString, {
    country: userCountry,
    options: { year: 'numeric', month: 'long', day: 'numeric' },
  });

  return (
    <div className="bg-white w-full max-w-[800px] mx-auto shadow-lg" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Header with colored bar */}
      <div className="text-white px-6 py-3 flex justify-between items-center" style={{ backgroundColor: primaryColor }}>
        <div>
          <span className="text-sm opacity-80">DOCUMENTO EQUIVALENTE A</span>
          <h1 className="text-2xl font-bold tracking-wide">CUENTA DE COBRO</h1>
        </div>
        <div className="text-right">
          <span className="text-2xl font-bold">N° {invoice.number || '---'}</span>
        </div>
      </div>

      <div className="p-6">
        {/* Date and City */}
        <div className="text-right text-gray-600 mb-6">
          <p>{companyInfo?.city || 'Ciudad'}, {formatDate(invoice.date)}</p>
        </div>

        {/* Two columns: Client Info (left) and Company/Creditor Info (right) */}
        <div className="flex justify-between mb-6">
          {/* Client Info - Who owes money (Left) - PAGO TOTAL */}
          <div className="w-1/2">
            <p className="text-sm font-semibold mb-2" style={{ color: primaryColor }}>PAGO TOTAL</p>
            {invoice.to?.logo && (
              <img src={invoice.to.logo} alt="Logo" className="h-16 mb-3 object-contain" />
            )}
            <div className="text-gray-800 whitespace-pre-line font-medium">
              {invoice.to?.name || '_______________'}
            </div>
          </div>

          {/* Company Info - Who is owed money (Right) - DEBE A */}
          <div className="w-1/2 text-right">
            <div className="p-4 inline-block text-left" style={{ backgroundColor: `${primaryColor}15`, borderLeft: `4px solid ${primaryColor}` }}>
              <p className="text-sm font-semibold mb-1" style={{ color: primaryColor }}>DEBE A:</p>
              <p className="font-bold text-gray-800">{companyInfo?.name || invoice.from?.name || '_______________'}</p>
              <p className="text-sm text-gray-600">NIT: {companyInfo?.nit || invoice.from?.nit || '_______________'}</p>
              <p className="text-sm text-gray-600">Tel: {companyInfo?.phone || invoice.from?.phone || '_______________'}</p>
            </div>
          </div>
        </div>

        {/* Amount in words */}
        <div className="bg-gray-50 p-4 rounded mb-6">
          <p className="text-sm text-gray-500 mb-1">LA SUMA DE:</p>
          <p className="font-bold text-gray-800 text-lg">
            {numeroALetras(Math.round(invoice.total || 0))} (${formatCurrency(invoice.total || 0)})
          </p>
        </div>

        {/* Concept Table */}
        <div className="mb-6">
          <div className="text-white px-4 py-2 font-semibold" style={{ backgroundColor: primaryColor }}>
            POR CONCEPTO DE:
          </div>
          <table className="w-full border-collapse table-fixed">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-700 w-16">CANT.</th>
                <th className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-gray-700">DESCRIPCIÓN</th>
                <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold text-gray-700 w-28">IMPORTE</th>
                <th className="border border-gray-300 px-3 py-2 text-right text-sm font-semibold text-gray-700 w-28">TOTAL</th>
              </tr>
            </thead>
            <tbody>
              {invoice.items && invoice.items.map((item, index) => (
                <tr key={index} className="bg-white">
                  <td className="border border-gray-300 px-3 py-2 text-center text-sm text-gray-800">
                    {String(item.quantity || 0)}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-sm text-gray-800" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {String(item.description || 'Sin descripción')}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-right text-sm text-gray-800">
                    ${formatCurrency(Number(item.rate) || 0)}
                  </td>
                  <td className="border border-gray-300 px-3 py-2 text-right text-sm text-gray-800">
                    ${formatCurrency(Number(item.amount) || 0)}
                  </td>
                </tr>
              ))}
              {(!invoice.items || invoice.items.length === 0) && (
                <tr className="bg-white">
                  <td colSpan="4" className="border border-gray-300 px-3 py-2 text-center text-sm text-gray-400">
                    Sin items
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Totals */}
          <div className="flex justify-end mt-2">
            <div className="w-64">
              <div className="flex justify-between py-1 border-b border-gray-200">
                <span className="text-sm text-gray-600">SUBTOTAL:</span>
                <span className="text-sm font-medium text-gray-800">
                  ${formatCurrency(invoice.subtotal || invoice.total || 0)}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-200">
                <span className="text-sm text-gray-600">IVA ({invoice.taxRate || 0}%):</span>
                <span className="text-sm font-medium text-gray-800">${formatCurrency(invoice.tax || 0)}</span>
              </div>
              <div className="flex justify-between py-2 text-white px-2 mt-1" style={{ backgroundColor: primaryColor }}>
                <span className="font-bold">TOTAL:</span>
                <span className="font-bold">${formatCurrency(invoice.total || 0)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes Section */}
        {invoice.notes && (
          <div className="mb-6 pl-4 py-2 bg-gray-50" style={{ borderLeft: `4px solid ${primaryColor}` }}>
            <p className="text-sm font-semibold text-gray-700 mb-1">NOTA:</p>
            <p className="text-sm text-gray-600">{invoice.notes}</p>
          </div>
        )}

        {/* Bank Details */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-sm font-semibold text-gray-700 mb-2">DATOS PARA PAGO:</p>
          <div className="flex gap-8 text-sm text-gray-600">
            <div>
              <p><span className="font-medium">Titular:</span> {companyInfo?.name || invoice.from?.name || '---'}</p>
              <p><span className="font-medium">NIT:</span> {companyInfo?.nit || invoice.from?.nit || '---'}</p>
            </div>
            <div>
              <p><span className="font-medium">Banco:</span> {companyInfo?.bank || invoice.from?.bank || '---'}</p>
              <p><span className="font-medium">Cuenta:</span> {(companyInfo?.accountType || invoice.from?.accountType) === 'checking' ? 'Corriente' : 'Ahorros'} - {companyInfo?.bankAccount || invoice.from?.bankAccount || '---'}</p>
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div className="mt-10 pt-6 border-t border-gray-200">
          <div className="flex justify-center">
            {/* Firma Acreedor (Empresa) */}
            <div className="w-64 text-center">
              <p className="text-xs text-gray-500 mb-2">FIRMA</p>
              {invoice.signature ? (
                <div className="mb-2 h-20 flex items-end justify-center">
                  <img 
                    src={invoice.signature} 
                    alt="Firma" 
                    className="max-h-20 max-w-[180px] object-contain"
                    style={{ 
                      transform: `rotate(${invoice.signatureRotation || 0}deg)`,
                      imageOrientation: 'from-image'
                    }}
                  />
                </div>
              ) : (
                <div className="mb-2 h-16"></div>
              )}
              <div className="border-t-2 border-gray-400 pt-2">
                <p className="text-sm font-medium text-gray-700">{companyInfo?.name || invoice.from?.name || '_______________'}</p>
                <p className="text-xs text-gray-500">NIT: {companyInfo?.nit || invoice.from?.nit || '_______________'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceTemplateCuentaCobro;
